// Mapping estático de slugs de categoría (sin dependencias de Prisma).
// Se separa de `routes.ts` para que se pueda importar desde middleware
// (edge runtime) y desde tests unitarios sin cargar la DB.
//
// El vocabulario de categorías es controlado y chico (~25 tags derivados
// del pipeline de `business-display.ts`). El mapping va acá para ser
// edge-safe.

export function slugify(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " y ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Slugs son plural cuando la categoría refiere al rubro del comercio
// (Pizzas → pizzerias, Panadería → panaderias) siguiendo la convención
// SEO local. Para etiquetas sin forma "rubro comercial" (Café, Sushi)
// se mantiene el label.
export const CATEGORY_SLUG_TO_LABEL: Record<string, string> = {
  pizzerias: "Pizzas",
  hamburgueserias: "Hamburguesas",
  heladerias: "Helados",
  panaderias: "Panadería",
  pastelerias: "Pastelería",
  sushi: "Sushi",
  empanadas: "Empanadas",
  milanesas: "Milanesas",
  pastas: "Pastas",
  parrilla: "Parrilla",
  sandwiches: "Sándwiches",
  tartas: "Tartas",
  cafeterias: "Café",
  saludable: "Saludable",
  picadas: "Picadas",
  pollos: "Pollo",
  "comida-china": "Comida China",
  mexicana: "Mexicana",
  arabe: "Árabe",
  wraps: "Wraps",
  sopas: "Sopas",
  carnes: "Carnes",
  pescados: "Pescados",
  postres: "Postres",
};

export const CATEGORY_LABEL_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_SLUG_TO_LABEL).map(([slug, label]) => [label, slug]),
);

// UI del hub — sustantivo humano para títulos y H1. Convención:
// nombrar por la comida en lugar del tipo de local ("Empanadas"
// no "Locales de empanadas", "Pizzas" no "Pizzerías"). Alinea con
// cómo la gente busca ("pizzas delivery" > "pizzerías delivery").
export const CATEGORY_SLUG_TO_HEADING: Record<string, string> = {
  pizzerias: "Pizzas",
  hamburgueserias: "Hamburguesas",
  heladerias: "Helados",
  panaderias: "Panadería",
  pastelerias: "Pastelería",
  sushi: "Sushi",
  empanadas: "Empanadas",
  milanesas: "Milanesas",
  pastas: "Pastas",
  parrilla: "Parrilla",
  sandwiches: "Sándwiches",
  tartas: "Tartas",
  cafeterias: "Café",
  saludable: "Comida saludable",
  picadas: "Picadas",
  pollos: "Pollo",
  "comida-china": "Comida china",
  mexicana: "Comida mexicana",
  arabe: "Comida árabe",
  wraps: "Wraps",
  sopas: "Sopas",
  carnes: "Carnes",
  pescados: "Pescados",
  postres: "Postres",
};

// Emoji ilustrativo por categoría — se usa en tiles visuales del hub
// de partido y en breadcrumbs mobile. No es exhaustivo — categorías sin
// emoji definido caen en un fallback genérico en la UI.
const CATEGORY_SLUG_TO_EMOJI: Record<string, string> = {
  pizzerias: "🍕",
  hamburgueserias: "🍔",
  heladerias: "🍦",
  panaderias: "🥐",
  pastelerias: "🍰",
  sushi: "🍣",
  empanadas: "🥟",
  milanesas: "🍗",
  pastas: "🍝",
  parrilla: "🍖",
  sandwiches: "🥪",
  tartas: "🥧",
  cafeterias: "☕",
  saludable: "🥗",
  picadas: "🧀",
  pollos: "🍗",
  "comida-china": "🥡",
  mexicana: "🌮",
  arabe: "🥙",
  wraps: "🌯",
  sopas: "🍜",
  carnes: "🥩",
  pescados: "🐟",
  postres: "🍮",
};

export function categorySlugToEmoji(slug: string): string {
  return CATEGORY_SLUG_TO_EMOJI[slug] ?? "🍽️";
}

// ─── Atributos dietéticos como eje de hub ───────────────────
//
// Segundo vocabulario reservado del segmento `/{partido}/{x}`, al lado
// de las categorías. Sale de `ComercioVidriera.dietas`, que el pipeline
// computa contando los productos de la carta que declaran el atributo
// (ver `dietas.ts`).
//
// Por qué por PARTIDO y no por localidad: medido sobre producción, sin
// TACC tiene 277 comercios en todo el catálogo. Partido es el nivel más
// chico donde la página todavía lista algo; en localidad quedarían
// decenas de páginas de un comercio.
//
// El wording no es casual. La detección lee lo que la carta DICE, no
// certifica la cocina — para alguien con celiaquía la contaminación
// cruzada es la diferencia entre poder comer o no. Por eso el heading
// afirma "opciones", nunca "apto celíacos".
export const DIETA_SLUG_TO_HEADING: Record<string, string> = {
  "sin-tacc": "Opciones sin TACC",
  vegano: "Opciones veganas",
  vegetariano: "Opciones vegetarianas",
  "sin-azucar": "Opciones sin azúcar",
};

/** Forma corta para chips y breadcrumbs, donde "Opciones" sobra. */
export const DIETA_SLUG_TO_CHIP: Record<string, string> = {
  "sin-tacc": "Sin TACC",
  vegano: "Vegano",
  vegetariano: "Vegetariano",
  "sin-azucar": "Sin azúcar",
};

export function isDietaSlug(slug: string): boolean {
  return slug in DIETA_SLUG_TO_HEADING;
}

export function dietaSlugToHeading(slug: string): string | null {
  return DIETA_SLUG_TO_HEADING[slug] ?? null;
}

export function isCategorySlug(slug: string): boolean {
  return slug in CATEGORY_SLUG_TO_LABEL;
}

export function categorySlugToLabel(slug: string): string | null {
  return CATEGORY_SLUG_TO_LABEL[slug] ?? null;
}

export function categorySlugToHeading(slug: string): string {
  return CATEGORY_SLUG_TO_HEADING[slug] ?? CATEGORY_SLUG_TO_LABEL[slug] ?? slug;
}

export function categoryLabelToSlug(label: string): string | null {
  return CATEGORY_LABEL_TO_SLUG[label] ?? null;
}

export function allCategorySlugs(): string[] {
  return Object.keys(CATEGORY_SLUG_TO_LABEL);
}

// Nombre humano del partido a partir del slug de peyaCity. El labelize
// genérico (Title Case del slug) alcanza para casi todos, pero hay
// partidos cuyo slug no reconstruye el nombre real — "3-de-febrero"
// labelizaría "3 De Febrero". Overrides explícitos para esos casos.
// Partidos cubiertos por el home/directorio — fuente de verdad
// CLIENT-SAFE (este módulo no toca Prisma). `home-cards-cache` la
// re-exporta para los consumidores server históricos.
export const HOME_COVERED_CITIES = [
  "san-isidro",
  "san-fernando",
  "tigre",
  "vicente-lopez",
  "buenos-aires",
  "pilar",
  "san-martin",
  "3-de-febrero",
  "moron",
  "la-matanza",
  "mar-del-plata",
] as const;

/** Lista {slug,label} para navegación: Buenos Aires primero, el resto
 *  alfabético. */
export function coveredPartidosNav(): Array<{ slug: string; label: string }> {
  const items = HOME_COVERED_CITIES.map((slug) => ({
    slug,
    label: partidoLabelFromSlug(slug),
  }));
  return [
    ...items.filter((i) => i.slug === "buenos-aires"),
    ...items
      .filter((i) => i.slug !== "buenos-aires")
      .sort((a, b) => a.label.localeCompare(b.label, "es")),
  ];
}

export const PARTIDO_LABEL_OVERRIDES: Record<string, string> = {
  "3-de-febrero": "Tres de Febrero",
  // El labelize genérico no repone tildes.
  moron: "Morón",
  "san-martin": "San Martín",
  "vicente-lopez": "Vicente López",
  // El labelize genérico capitaliza cada palabra: "Mar Del Plata".
  "mar-del-plata": "Mar del Plata",
};

export function partidoLabelFromSlug(slug: string): string {
  return (
    PARTIDO_LABEL_OVERRIDES[slug] ??
    slug.replaceAll("-", " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
